/**
 * PricingTable — the professional pricing-table UI for the platform pricing console. A responsive auto-fit
 * grid of tier CARDS (starter → standard → pro → enterprise), each showing the tier badge, the monthly price
 * (mono + LTR island so figures read identically FR/AR), the annual note, the included modules + cloud SKUs
 * as a checklist, and the per-plan operator actions (assign / checkout / edit / retire|restore). The "pro"
 * tier is highlighted as the recommended plan (accent border + a "most popular" ribbon), the classic
 * pricing-page emphasis. Retired plans render dimmed with a Retiré badge and a Restore action.
 *
 * Token-driven throughout (no raw hex, L-2). AA contrast: every action is a real <Button> with text (never
 * icon-only), the recommended emphasis is carried by BOTH an accent border AND a text ribbon (never
 * color-only), and each card is an <article> with an accessible heading. Purely presentational — all state +
 * mutations live in the parent PricingPage; this only renders + raises callbacks.
 */
import { Badge, Button, Card } from "@/ui";
import { useT } from "@/app";
import { formatCentimes } from "../screens/pricing.lib";
import type { PlanRow, PlanTier } from "../screens/pricing.types";

/** The tier we visually recommend (the classic "most popular" emphasis). */
const RECOMMENDED_TIER: PlanTier = "pro";

export interface PricingTableProps {
  plans: ReadonlyArray<PlanRow>;
  onAssign: (plan: PlanRow) => void;
  onCheckout: (plan: PlanRow) => void;
  onEdit: (plan: PlanRow) => void;
  onRetire: (plan: PlanRow) => void;
  onRestore: (plan: PlanRow) => void;
}

export function PricingTable({ plans, onAssign, onCheckout, onEdit, onRetire, onRestore }: PricingTableProps) {
  return (
    <div
      style={{
        display: "grid",
        gap: "var(--nv-s-4)",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        alignItems: "stretch",
      }}
    >
      {plans.map((plan) => (
        <PlanCard
          key={plan.code}
          plan={plan}
          onAssign={onAssign}
          onCheckout={onCheckout}
          onEdit={onEdit}
          onRetire={onRetire}
          onRestore={onRestore}
        />
      ))}
    </div>
  );
}

function PlanCard({
  plan,
  onAssign,
  onCheckout,
  onEdit,
  onRetire,
  onRestore,
}: { plan: PlanRow } & Omit<PricingTableProps, "plans">) {
  const { t } = useT();
  const recommended = plan.actif && plan.tier === RECOMMENDED_TIER;
  const retired = !plan.actif;

  return (
    <article
      aria-label={plan.nom}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        border: recommended
          ? "2px solid var(--nv-accent, var(--nv-azure-600))"
          : "1px solid var(--nv-border-subtle, var(--nv-line))",
        borderRadius: "var(--nv-r-lg)",
        background: "var(--nv-surface)",
        boxShadow: recommended ? "var(--nv-e2)" : "var(--nv-e1)",
        opacity: retired ? 0.72 : 1,
        overflow: "hidden",
      }}
    >
      {recommended && (
        <div
          style={{
            background: "var(--nv-accent, var(--nv-azure-600))",
            color: "var(--nv-white)",
            textAlign: "center",
            fontSize: "var(--nv-fs-caption)",
            fontWeight: 600,
            padding: "var(--nv-s-1) var(--nv-s-2)",
          }}
        >
          {t("mgmt.pricing.mostPopular")}
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--nv-s-3)",
          padding: "var(--nv-s-4)",
          flex: 1,
        }}
      >
        {/* Tier + name + retired flag */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)", flexWrap: "wrap" }}>
            <Badge tone={recommended ? "info" : "neutral"}>{t(`mgmt.pricing.tier.${plan.tier}`)}</Badge>
            {retired && <Badge tone="danger">{t("mgmt.pricing.retiredBadge")}</Badge>}
          </div>
          <h3 style={{ margin: 0, fontSize: "var(--nv-fs-h3)" }}>{plan.nom}</h3>
          <span className="nv-ltr-island" style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
            {plan.code}
          </span>
          {plan.description && (
            <p style={{ margin: 0, fontSize: "var(--nv-fs-body)", color: "var(--nv-text-2, var(--nv-slate))" }}>
              {plan.description}
            </p>
          )}
        </div>

        {/* Price */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "var(--nv-s-2)", flexWrap: "wrap" }}>
            <span
              className="nv-ltr-island"
              style={{
                fontFamily: "var(--nv-font-mono)",
                fontWeight: 700,
                fontSize: "var(--nv-fs-display, var(--nv-fs-h1))",
                lineHeight: 1.1,
                color: "var(--nv-text-0, var(--nv-ink))",
              }}
            >
              {formatCentimes(plan.prixMensuelCentimes)}
            </span>
            <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
              {t("mgmt.pricing.perMonth")}
            </span>
          </div>
          <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
            {plan.prixAnnuelCentimes != null
              ? t("mgmt.pricing.annualNote", { price: formatCentimes(plan.prixAnnuelCentimes) })
              : t("mgmt.pricing.noAnnual")}
          </span>
        </div>

        {/* Modules checklist */}
        <FeatureList
          title={t("mgmt.pricing.modulesTitle")}
          items={plan.modules}
          emptyLabel={t("mgmt.pricing.noModules")}
        />
        {plan.cloudSku.length > 0 && <FeatureList title={t("mgmt.pricing.cloudTitle")} items={plan.cloudSku} />}
      </div>

      {/* Actions */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--nv-s-2)",
          padding: "var(--nv-s-4)",
          borderTop: "1px solid var(--nv-border-subtle, var(--nv-line-faint))",
        }}
      >
        {!retired && (
          <>
            <Button variant="primary" onClick={() => onAssign(plan)}>
              {t("mgmt.pricing.card.assign")}
            </Button>
            <Button variant="secondary" onClick={() => onCheckout(plan)}>
              {t("mgmt.pricing.card.checkout")}
            </Button>
          </>
        )}
        <div style={{ display: "flex", gap: "var(--nv-s-2)" }}>
          <Button size="sm" variant="ghost" onClick={() => onEdit(plan)} style={{ flex: 1 }}>
            {t("mgmt.pricing.card.edit")}
          </Button>
          {retired ? (
            <Button size="sm" variant="ghost" onClick={() => onRestore(plan)} style={{ flex: 1 }}>
              {t("mgmt.pricing.card.restore")}
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => onRetire(plan)} style={{ flex: 1 }}>
              {t("mgmt.pricing.card.retire")}
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}

/** A labelled checklist of included items (modules / cloud SKUs), or a muted empty line. */
function FeatureList({ title, items, emptyLabel }: { title: string; items: string[]; emptyLabel?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
      <span style={{ fontSize: "var(--nv-fs-label)", fontWeight: 600, color: "var(--nv-text-2, var(--nv-slate))" }}>
        {title}
      </span>
      {items.length === 0 && emptyLabel ? (
        <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>{emptyLabel}</span>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
          {items.map((item) => (
            <li key={item} style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)", fontSize: "var(--nv-fs-body)" }}>
              <span aria-hidden style={{ color: "var(--nv-success, var(--nv-normal))", fontWeight: 700 }}>
                ✓
              </span>
              <span className="nv-ltr-island">{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Card-wrapped variant kept for a section that wants the pricing grid inside a titled Card. */
export function PricingTableCard(props: PricingTableProps & { title: string; subtitle?: string }) {
  const { title, subtitle, ...table } = props;
  return (
    <Card title={title}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
        {subtitle && (
          <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>{subtitle}</p>
        )}
        <PricingTable {...table} />
      </div>
    </Card>
  );
}
